import { RIGHT_DRAWER } from '@flexo/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { IconWrapper } from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import { useTranslation } from 'react-i18next';
import styles from './right-drawer-content.module.scss';
import { FILES } from '@flexo/reducers';
import { useEffect, useState } from 'react';
import { openPdfWithTitle } from 'libs/atoms/src/lib/table/helpers/helpers-types';
import { Player } from '@lottiefiles/react-lottie-player';
import { usePrivacyPolicy } from '@flexo/hooks';

const loader = '/loaderLottie.json';
const ENV = (
  import.meta as unknown as { env: Record<string, string | undefined> }
).env;
const LEGAL_LINKS = [
  {
    labelKey: 'pages.profile.about.privacy_policy',
    urlKey: 'privacy_policy_url',
  },
  {
    labelKey: 'pages.profile.about.terms_and_conditions',
    urlKey: 'terms_and_conditions_url',
  },
  {
    labelKey: 'pages.profile.about.rcp_member_entry_exit_notification',
    urlKey: 'rcp_member_entry_exit_notification_url',
  },
];

interface InformationFile {
  file_name?: string;
  url?: string;
  file_base64?: string;
  file?: string;
}

interface FilesState {
  FetchFilesOncePerDayAsyncStatus?: string;
  files?: InformationFile[] | { files?: InformationFile[] }[];
}

export function InformationsDrawerContent() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const files = useSelector((state: { files?: FilesState }) => state?.files);
  const [filesList, setFilesList] = useState<InformationFile[]>([]);
  const { policy } = usePrivacyPolicy<Record<string, unknown>>();

  const legalLinks = LEGAL_LINKS.map((link) => {
    const label = t(link.labelKey, { defaultValue: '' });
    const url = policy?.[link.urlKey];

    if (!label || typeof url !== 'string' || !url) {
      return null;
    }

    return {
      label,
      url,
      urlKey: link.urlKey,
    };
  }).filter(Boolean) as { label: string; url: string; urlKey: string }[];

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  useEffect(() => {
    async function fetchFiles() {
      const fetchFilesAction = FILES.FetchFilesOncePerDayAsync({
        useMockData: false,
      }) as unknown as Parameters<typeof dispatch>[0];

      dispatch(fetchFilesAction);
    }
    fetchFiles();
  }, [dispatch]);

  useEffect(() => {
    if (files?.FetchFilesOncePerDayAsyncStatus !== 'SUCCESS') {
      return;
    }

    const rawFiles = files?.files;
    if (!Array.isArray(rawFiles)) {
      setFilesList([]);
      return;
    }

    const nestedFiles = (rawFiles[0] as { files?: InformationFile[] } | undefined)
      ?.files;
    if (Array.isArray(nestedFiles)) {
      setFilesList(nestedFiles);
      return;
    }

    setFilesList(rawFiles as InformationFile[]);
  }, [files]);

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.RightDrawerContent__Title__Custom}>
        <div>
          <p className={styles.RightDrawerContent__Paragraph}>
            {t('pages.informations.title')}
          </p>
          <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
        </div>
        <p className={styles.RightDrawerContent__Title__Custom__Paragraph}>
          {t('pages.informations.subtitle')}
        </p>
      </div>
      <div className={styles.RightDrawerContent__Body__Custom}>
        {files?.FetchFilesOncePerDayAsyncStatus === 'PENDING' && (
          <div className={styles.FilesList__Loading}>
            <Player autoplay loop src={loader} style={{ height: 20 }} />
          </div>
        )}
        {files?.FetchFilesOncePerDayAsyncStatus === 'ERROR' && (
          <div className={styles.FilesList__Loading}>
            <p>{t('pages.informations.no_information_files')}</p>
          </div>
        )}
        {files?.FetchFilesOncePerDayAsyncStatus === 'SUCCESS' &&
          filesList.length === 0 && (
            <div className={styles.FilesList__Loading}>
              <p>{t('pages.informations.no_information_files')}</p>
            </div>
          )}
        {filesList.map((file, file_i) => (
          <div key={`file-${file_i}`}>
            <div
              className={`${styles.FilesList__Item} ${file_i === 0 && 'mt-2'}`}
              onClick={() => {
                if (file.url) {
                  window.open(file.url, '_blank');
                } else if (file.file_base64) {
                  openPdfWithTitle(file.file_base64, file.file);
                } else {
                  return;
                }
              }}
            >
              <div className={styles.FilesList__Item__Name}>
                <p>{file.file_name}</p>
              </div>
              <div className={styles.FilesList__Item__Actions}>
                <IconWrapper iconName="chevron-right" />
              </div>
            </div>
          </div>
        ))}
        {legalLinks.map((link, link_i) => (
          <div key={`legal-link-${link.urlKey}`}>
            <div
              className={`${styles.FilesList__Item} ${
                filesList.length === 0 && link_i === 0 ? 'mt-2' : ''
              }`}
              onClick={() => window.open(link.url, '_blank')}
            >
              <div className={styles.FilesList__Item__Name}>
                <p>{link.label}</p>
              </div>
              <div className={styles.FilesList__Item__Actions}>
                <IconWrapper iconName="chevron-right" />
              </div>
            </div>
          </div>
        ))}
        <div className={styles.FilesList__Application_Infos}>
          <div className={styles.FilesList__Application_Infos__Logo}>
            <IconWrapper
              iconName={ENV?.VITE_APP_IS_WEB === 'true' ? 'logo_wide' : 'logo'}
            />
          </div>
          <div className={styles.FilesList__Application_Infos__Version}>
            <p>
              {t('pages.informations.version')} {ENV.VITE_APP_VERSION}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
